import { useState } from 'react';
import {
	useQuery,
	useMutation,
} from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/components/ui/alert';

interface PlatformConnection {
	id: string;
	platform: 'youtube' | 'twitch' | 'facebook';
	platformAccountName: string;
	status: 'active' | 'revoked' | 'expired';
	createdAt: string;
}

export default function PlatformConnections() {
	const { toast } = useToast();
	const [loading, setLoading] = useState<string>('');

	// Get the current organization
	const { data: organizations } = useQuery<
		Array<{ id: string; name: string }>
	>({
		queryKey: ['/api/organizations'],
	});
	const currentOrg = organizations?.[0];

	// Get platform connections
	const {
		data: connections,
		isLoading: connectionsLoading,
	} = useQuery<PlatformConnection[]>({
		queryKey: [
			'/api/organizations',
			currentOrg?.id,
			'platforms',
		],
		enabled: !!currentOrg?.id,
	});

	// Delete connection mutation
	const deleteMutation = useMutation({
		mutationFn: async ({
			platform,
			connectionId,
		}: {
			platform: string;
			connectionId: string;
		}) => {
			const res = await apiRequest(
				'DELETE',
				`/api/organizations/${currentOrg?.id}/platforms/${platform}/${connectionId}`
			);
			return res.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [
					'/api/organizations',
					currentOrg?.id,
					'platforms',
				],
			});
			toast({
				title: 'Success',
				description: 'Platform connection removed',
			});
		},
		onError: (error: Error) => {
			toast({
				title: 'Error',
				description:
					error.message ||
					'Failed to remove platform connection',
				variant: 'destructive',
			});
		},
	});

	// Start OAuth flow
	const handleConnect = async (platform: string) => {
		setLoading(platform);
		try {
			const res = await apiRequest(
				'POST',
				`/api/organizations/${currentOrg?.id}/platforms/${platform}/connect`
			);
			const { authUrl } = await res.json();

			if (authUrl) {
				window.location.href = authUrl;
			}
		} catch (error) {
			toast({
				title: 'Error',
				description: 'Failed to start platform connection',
				variant: 'destructive',
			});
		}
		setLoading('');
	};

	// Handle connection deletion
	const handleDisconnect = (
		platform: string,
		connectionId: string
	) => {
		if (
			confirm(
				'Are you sure you want to disconnect this platform?'
			)
		) {
			deleteMutation.mutate({ platform, connectionId });
		}
	};

	if (!currentOrg) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Platform Connections</CardTitle>
					<CardDescription>
						Loading organization information...
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	if (connectionsLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Platform Connections</CardTitle>
					<CardDescription>
						Loading platform connections...
					</CardDescription>
				</CardHeader>
			</Card>
		);
	}

	return (
		<div className='space-y-6'>
			<Card>
				<CardHeader>
					<CardTitle>Platform Connections</CardTitle>
					<CardDescription>
						Connect your streaming platforms to manage your
						streams
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className='space-y-4'>
						{/* YouTube */}
						<div className='flex items-center justify-between p-4 border rounded-lg'>
							<div className='flex items-center space-x-4'>
								<img
									src='/youtube-icon.png'
									alt='YouTube'
									className='w-8 h-8'
								/>
								<div>
									<h3 className='font-medium'>YouTube</h3>
									<p className='text-sm text-muted-foreground'>
										Stream directly to YouTube
									</p>
								</div>
							</div>
							{connections?.find(
								(c) =>
									c.platform === 'youtube' &&
									c.status === 'active'
							) ? (
								<div className='flex items-center space-x-4'>
									<Alert
										variant='success'
										className='w-auto'
									>
										<CheckCircle2 className='h-4 w-4' />
										<AlertTitle>Connected</AlertTitle>
										<AlertDescription>
											{
												connections.find(
													(c) => c.platform === 'youtube'
												)?.platformAccountName
											}
										</AlertDescription>
									</Alert>
									<Button
										variant='destructive'
										size='sm'
										onClick={() =>
											handleDisconnect(
												'youtube',
												connections.find(
													(c) => c.platform === 'youtube'
												)!.id
											)
										}
									>
										Disconnect
									</Button>
								</div>
							) : (
								<Button
									onClick={() => handleConnect('youtube')}
									disabled={loading === 'youtube'}
								>
									{loading === 'youtube'
										? 'Connecting...'
										: 'Connect'}
								</Button>
							)}
						</div>

						{/* Twitch */}
						<div className='flex items-center justify-between p-4 border rounded-lg'>
							<div className='flex items-center space-x-4'>
								<img
									src='/twitch-icon.png'
									alt='Twitch'
									className='w-8 h-8'
								/>
								<div>
									<h3 className='font-medium'>Twitch</h3>
									<p className='text-sm text-muted-foreground'>
										Stream directly to Twitch
									</p>
								</div>
							</div>
							{connections?.find(
								(c) =>
									c.platform === 'twitch' &&
									c.status === 'active'
							) ? (
								<div className='flex items-center space-x-4'>
									<Alert
										variant='success'
										className='w-auto'
									>
										<CheckCircle2 className='h-4 w-4' />
										<AlertTitle>Connected</AlertTitle>
										<AlertDescription>
											{
												connections.find(
													(c) => c.platform === 'twitch'
												)?.platformAccountName
											}
										</AlertDescription>
									</Alert>
									<Button
										variant='destructive'
										size='sm'
										onClick={() =>
											handleDisconnect(
												'twitch',
												connections.find(
													(c) => c.platform === 'twitch'
												)!.id
											)
										}
									>
										Disconnect
									</Button>
								</div>
							) : (
								<Button
									onClick={() => handleConnect('twitch')}
									disabled={loading === 'twitch'}
								>
									{loading === 'twitch'
										? 'Connecting...'
										: 'Connect'}
								</Button>
							)}
						</div>

						{/* Facebook */}
						<div className='flex items-center justify-between p-4 border rounded-lg'>
							<div className='flex items-center space-x-4'>
								<img
									src='/facebook-icon.png'
									alt='Facebook'
									className='w-8 h-8'
								/>
								<div>
									<h3 className='font-medium'>Facebook</h3>
									<p className='text-sm text-muted-foreground'>
										Stream directly to Facebook
									</p>
								</div>
							</div>
							{connections?.find(
								(c) =>
									c.platform === 'facebook' &&
									c.status === 'active'
							) ? (
								<div className='flex items-center space-x-4'>
									<Alert
										variant='success'
										className='w-auto'
									>
										<CheckCircle2 className='h-4 w-4' />
										<AlertTitle>Connected</AlertTitle>
										<AlertDescription>
											{
												connections.find(
													(c) => c.platform === 'facebook'
												)?.platformAccountName
											}
										</AlertDescription>
									</Alert>
									<Button
										variant='destructive'
										size='sm'
										onClick={() =>
											handleDisconnect(
												'facebook',
												connections.find(
													(c) => c.platform === 'facebook'
												)!.id
											)
										}
									>
										Disconnect
									</Button>
								</div>
							) : (
								<Button
									onClick={() => handleConnect('facebook')}
									disabled={loading === 'facebook'}
								>
									{loading === 'facebook'
										? 'Connecting...'
										: 'Connect'}
								</Button>
							)}
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
